import { defineMessage, MessageDescriptor } from 'react-intl'

import { DA_CARE_ONBOARDING_HPI_PAYMENT_CONTENT_NAME } from './careOnboardingHpiPayment'
import { careConsentFormError } from '../common/constants/messages'

const mentalHealthEvaluationOnboardingTitle = defineMessage({
  defaultMessage: 'Looking forward to our mental health evaluation, {clientFirstName}.',
  description: 'Title on the care onboarding form welcoming a member to their mental health evaluation',
})

export type ContentAttributes = {
  bypassAssessmentSummary?: boolean
  bypassReview?: boolean
  careOnboardingTitle?: MessageDescriptor
  customActivityIntroduction?: string
  customErrorMessage?: { defaultMessage: string; description: string }
  enableBackNavigation?: boolean
  group?: string
  hideInClientActivities?: boolean
  hideInstructionsIfHasPrereq?: boolean
  hideProgressBar?: boolean
  hideSubmitButton?: boolean
  initialPageIfHasPrereq?: number
  linkedAssignmentTitle?: string
  linkedStatusAssignments?: (string | string[])[]
  linkedVideo?: string
  maxWidthSubmitButton?: boolean
  shownParentActivity?: string | string[]
  showFullProgressOnSubmit?: boolean
  singlePageContent?: boolean
}

export const customContentAttributes: {
  [key: string]: ContentAttributes
} = {
  audCareConsent: {
    bypassAssessmentSummary: true,
    bypassReview: true,
    customActivityIntroduction: 'CareOnboardingHeader',
    customErrorMessage: careConsentFormError,
    hideInClientActivities: true,
    linkedAssignmentTitle: 'Care Onboarding',
    hideProgressBar: true,
    linkedStatusAssignments: ['audCareConsent', 'audClientIntake', 'audInitialAssessmentMetadata'],
    singlePageContent: true,
    shownParentActivity: 'audClientIntake',
  },
  audClientIntake: {
    bypassAssessmentSummary: true,
    bypassReview: true,
    linkedAssignmentTitle: 'Care Onboarding',
    hideInstructionsIfHasPrereq: true,
    initialPageIfHasPrereq: 1,
    linkedStatusAssignments: ['audCareConsent', 'audClientIntake', 'audInitialAssessmentMetadata'],
  },
  audInitialAssessmentMetadata: {
    bypassAssessmentSummary: false,
    bypassReview: true,
    linkedAssignmentTitle: 'Care Onboarding',
    hideInClientActivities: true,
    hideInstructionsIfHasPrereq: true,
    initialPageIfHasPrereq: 1,
    linkedStatusAssignments: ['audCareConsent', 'audClientIntake', 'audInitialAssessmentMetadata'],
    linkedVideo: 'baseYourTherapyWithLyra',
    shownParentActivity: 'audClientIntake',
  },
  audSudExpansionCareConsent: {
    bypassAssessmentSummary: true,
    bypassReview: true,
    customActivityIntroduction: 'CareOnboardingHeader',
    customErrorMessage: careConsentFormError,
    hideInClientActivities: true,
    linkedAssignmentTitle: 'Care Onboarding',
    hideProgressBar: true,
    linkedStatusAssignments: [
      'audSudExpansionCareConsent',
      'audSudExpansionClientIntake',
      'audSudExpansionInitialAssessmentMetadata',
    ],
    singlePageContent: true,
    shownParentActivity: 'audSudExpansionClientIntake',
  },
  audSudExpansionClientIntake: {
    bypassAssessmentSummary: true,
    bypassReview: true,
    linkedAssignmentTitle: 'Care Onboarding',
    hideInstructionsIfHasPrereq: true,
    initialPageIfHasPrereq: 1,
    linkedStatusAssignments: [
      'audSudExpansionCareConsent',
      'audSudExpansionClientIntake',
      'audSudExpansionInitialAssessmentMetadata',
    ],
  },
  audSudExpansionInitialAssessmentMetadata: {
    bypassAssessmentSummary: false,
    bypassReview: true,
    linkedAssignmentTitle: 'Care Onboarding',
    hideInClientActivities: true,
    hideInstructionsIfHasPrereq: true,
    initialPageIfHasPrereq: 1,
    linkedStatusAssignments: [
      'audSudExpansionCareConsent',
      'audSudExpansionClientIntake',
      'audSudExpansionInitialAssessmentMetadata',
    ],
    linkedVideo: 'baseYourTherapyWithLyra',
    shownParentActivity: 'audSudExpansionClientIntake',
  },
  baseActionPlan: {
    hideProgressBar: true,
    hideSubmitButton: true,
    singlePageContent: true,
  },
  baseYourCoachingWithLyra: {
    group: 'lessonPreviewMaximized',
  },
  baseYourTherapyWithLyra: {
    group: 'lessonPreviewMaximized',
  },
  bccCareConsent: {
    bypassAssessmentSummary: true,
    bypassReview: true,
    customActivityIntroduction: 'CareOnboardingHeader',
    customErrorMessage: careConsentFormError,
    hideInClientActivities: true,
    linkedAssignmentTitle: 'Care Onboarding',
    hideProgressBar: true,
    linkedStatusAssignments: ['bccCareConsent', 'bccClientIntake', 'bccBaselineMetadata'],
    singlePageContent: true,
    shownParentActivity: 'bccClientIntake',
  },
  bccClientIntake: {
    bypassAssessmentSummary: true,
    bypassReview: true,
    linkedAssignmentTitle: 'Care Onboarding',
    hideInstructionsIfHasPrereq: true,
    initialPageIfHasPrereq: 1,
    linkedStatusAssignments: ['bccCareConsent', 'bccClientIntake', 'bccBaselineMetadata'],
  },
  bccBaselineMetadata: {
    bypassAssessmentSummary: false,
    bypassReview: true,
    linkedAssignmentTitle: 'Care Onboarding',
    hideInClientActivities: true,
    hideInstructionsIfHasPrereq: true,
    initialPageIfHasPrereq: 1,
    linkedStatusAssignments: ['bccCareConsent', 'bccClientIntake', 'bccBaselineMetadata'],
    linkedVideo: 'baseYourCoachingWithLyra',
    shownParentActivity: 'bccClientIntake',
  },
  bcmCareConsent: {
    bypassAssessmentSummary: true,
    bypassReview: true,
    customActivityIntroduction: 'CareOnboardingHeader',
    customErrorMessage: careConsentFormError,
    hideInClientActivities: true,
    linkedAssignmentTitle: 'Client Intake',
    hideProgressBar: true,
    linkedStatusAssignments: ['bcmCareConsent', 'bcmClientIntake', 'baseBaselineExtendedMetadata'],
    singlePageContent: true,
    shownParentActivity: 'bcmClientIntake',
  },
  bcmClientIntake: {
    bypassAssessmentSummary: true,
    bypassReview: true,
    linkedAssignmentTitle: 'Client Intake',
    hideInstructionsIfHasPrereq: true,
    initialPageIfHasPrereq: 1,
    linkedStatusAssignments: ['bcmCareConsent', 'bcmClientIntake', 'baseBaselineExtendedMetadata'],
  },
  baseBaselineExtendedMetadata: {
    bypassAssessmentSummary: false,
    bypassReview: true,
    linkedAssignmentTitle: 'Client Intake',
    hideInClientActivities: true,
    hideInstructionsIfHasPrereq: true,
    initialPageIfHasPrereq: 1,
    linkedStatusAssignments: ['bcmCareConsent', 'bcmClientIntake', 'baseBaselineExtendedMetadata'],
    shownParentActivity: 'bcmClientIntake',
  },
  bctCareConsent: {
    bypassAssessmentSummary: true,
    bypassReview: true,
    customActivityIntroduction: 'CareOnboardingHeader',
    customErrorMessage: careConsentFormError,
    hideInClientActivities: true,
    linkedAssignmentTitle: 'Care Onboarding',
    hideProgressBar: true,
    linkedStatusAssignments: ['bctCareConsent', 'bctClientIntake', 'baseInitialAssessmentMetadata'],
    singlePageContent: true,
    shownParentActivity: 'bctClientIntake',
  },
  bctClientIntake: {
    bypassAssessmentSummary: true,
    bypassReview: true,
    linkedAssignmentTitle: 'Care Onboarding',
    hideInstructionsIfHasPrereq: true,
    initialPageIfHasPrereq: 1,
    linkedStatusAssignments: ['bctCareConsent', 'bctClientIntake', 'baseInitialAssessmentMetadata'],
  },
  baseInitialAssessmentMetadata: {
    bypassAssessmentSummary: false,
    bypassReview: true,
    linkedAssignmentTitle: 'Care Onboarding',
    hideInClientActivities: true,
    hideInstructionsIfHasPrereq: true,
    initialPageIfHasPrereq: 1,
    linkedStatusAssignments: ['bctCareConsent', 'bctClientIntake', 'baseInitialAssessmentMetadata'],
    linkedVideo: 'baseYourTherapyWithLyra',
    shownParentActivity: 'bctClientIntake',
  },
  baseReleaseHealthRecord: {
    bypassReview: true,
    enableBackNavigation: true,
    maxWidthSubmitButton: true,
    showFullProgressOnSubmit: true,
  },
  cleCareConsent: {
    bypassAssessmentSummary: true,
    bypassReview: true,
    careOnboardingTitle: mentalHealthEvaluationOnboardingTitle,
    customActivityIntroduction: 'CareOnboardingHeader',
    customErrorMessage: careConsentFormError,
    hideInClientActivities: true,
    linkedAssignmentTitle: 'Evaluation onboarding',
    hideProgressBar: true,
    linkedStatusAssignments: ['cleCareConsent', 'cleClientIntake', 'cleInitialAssessment'],
    singlePageContent: true,
    shownParentActivity: 'cleClientIntake',
  },
  cleClientIntake: {
    bypassAssessmentSummary: true,
    bypassReview: true,
    linkedAssignmentTitle: 'Evaluation onboarding',
    hideInstructionsIfHasPrereq: true,
    initialPageIfHasPrereq: 1,
    linkedStatusAssignments: ['cleCareConsent', 'cleClientIntake', 'cleInitialAssessment'],
  },
  cleInitialAssessment: {
    bypassAssessmentSummary: false,
    bypassReview: true,
    linkedAssignmentTitle: 'Evaluation onboarding',
    hideInClientActivities: true,
    hideInstructionsIfHasPrereq: true,
    initialPageIfHasPrereq: 1,
    linkedStatusAssignments: ['cleCareConsent', 'cleClientIntake', 'cleInitialAssessment'],
    linkedVideo: 'baseYourTherapyWithLyra',
    shownParentActivity: 'cleClientIntake',
  },
  sapCareConsent: {
    bypassAssessmentSummary: true,
    bypassReview: true,
    customActivityIntroduction: 'CareOnboardingHeader',
    customErrorMessage: careConsentFormError,
    hideInClientActivities: true,
    linkedAssignmentTitle: 'Client Intake',
    hideProgressBar: true,
    linkedStatusAssignments: ['sapCareConsent', 'sapClientIntake', 'sapInitialAssessment'],
    singlePageContent: true,
    shownParentActivity: 'sapClientIntake',
  },
  sapClientIntake: {
    bypassAssessmentSummary: true,
    bypassReview: true,
    linkedAssignmentTitle: 'Client Intake',
    hideInstructionsIfHasPrereq: true,
    initialPageIfHasPrereq: 1,
    linkedStatusAssignments: ['sapCareConsent', 'sapClientIntake', 'sapInitialAssessment'],
  },
  sapInitialAssessment: {
    bypassAssessmentSummary: false,
    bypassReview: true,
    linkedAssignmentTitle: 'Client Intake',
    hideInClientActivities: true,
    hideInstructionsIfHasPrereq: true,
    initialPageIfHasPrereq: 1,
    linkedStatusAssignments: ['sapCareConsent', 'sapClientIntake', 'sapInitialAssessment'],
    linkedVideo: 'baseYourTherapyWithLyra',
    shownParentActivity: 'sapClientIntake',
  },
  daCareConsent: {
    bypassAssessmentSummary: true,
    bypassReview: true,
    customActivityIntroduction: 'CareOnboardingHeader',
    customErrorMessage: careConsentFormError,
    hideInClientActivities: true,
    linkedAssignmentTitle: 'Care Onboarding',
    hideProgressBar: true,
    linkedStatusAssignments: [
      'daCareConsent',
      DA_CARE_ONBOARDING_HPI_PAYMENT_CONTENT_NAME,
      [
        'daAdolescentClientIntake',
        'daAdolescentGuardianClientIntake',
        'daChildClientIntake',
        'daCouplesClientIntake',
        'daFamilyClientIntake',
        'daIndividualClientIntake',
      ],
      'daInitialAssessment',
    ],
    shownParentActivity: [
      'daAdolescentClientIntake',
      'daAdolescentGuardianClientIntake',
      'daChildClientIntake',
      'daCouplesClientIntake',
      'daFamilyClientIntake',
      'daIndividualClientIntake',
    ],
    singlePageContent: true,
  },
  daAdolescentClientIntake: {
    bypassAssessmentSummary: true,
    bypassReview: true,
    linkedAssignmentTitle: 'Care Onboarding',
    initialPageIfHasPrereq: 1,
    linkedStatusAssignments: ['daCareConsent', 'daAdolescentClientIntake', 'daInitialAssessment'],
  },
  daAdolescentGuardianClientIntake: {
    bypassAssessmentSummary: true,
    bypassReview: true,
    linkedAssignmentTitle: 'Care Onboarding',
    initialPageIfHasPrereq: 1,
    linkedStatusAssignments: ['daCareConsent', 'daAdolescentGuardianClientIntake', 'daInitialAssessment'],
  },
  daChildClientIntake: {
    bypassAssessmentSummary: true,
    bypassReview: true,
    linkedAssignmentTitle: 'Care Onboarding',
    initialPageIfHasPrereq: 1,
    linkedStatusAssignments: ['daCareConsent', 'daChildClientIntake', 'daInitialAssessment'],
  },
  daCouplesClientIntake: {
    bypassAssessmentSummary: true,
    bypassReview: true,
    linkedAssignmentTitle: 'Care Onboarding',
    initialPageIfHasPrereq: 1,
    linkedStatusAssignments: [
      'daCareConsent',
      DA_CARE_ONBOARDING_HPI_PAYMENT_CONTENT_NAME,
      'daCouplesClientIntake',
      'daInitialAssessment',
    ],
  },
  daFamilyClientIntake: {
    bypassAssessmentSummary: true,
    bypassReview: true,
    linkedAssignmentTitle: 'Care Onboarding',
    initialPageIfHasPrereq: 1,
    linkedStatusAssignments: [
      'daCareConsent',
      DA_CARE_ONBOARDING_HPI_PAYMENT_CONTENT_NAME,
      'daFamilyClientIntake',
      'daInitialAssessment',
    ],
  },
  daIndividualClientIntake: {
    bypassAssessmentSummary: true,
    bypassReview: true,
    linkedAssignmentTitle: 'Care Onboarding',
    initialPageIfHasPrereq: 1,
    linkedStatusAssignments: [
      'daCareConsent',
      DA_CARE_ONBOARDING_HPI_PAYMENT_CONTENT_NAME,
      'daIndividualClientIntake',
      'daInitialAssessment',
    ],
  },
  [DA_CARE_ONBOARDING_HPI_PAYMENT_CONTENT_NAME]: {
    bypassAssessmentSummary: true,
    bypassReview: true,
    hideInClientActivities: true,
    hideProgressBar: true,
    linkedAssignmentTitle: 'Care Onboarding',
    shownParentActivity: ['daCouplesClientIntake', 'daFamilyClientIntake', 'daIndividualClientIntake'],
    linkedStatusAssignments: [
      'daCareConsent',
      DA_CARE_ONBOARDING_HPI_PAYMENT_CONTENT_NAME,
      ['daCouplesClientIntake', 'daFamilyClientIntake', 'daIndividualClientIntake'],
      'daInitialAssessment',
    ],
  },
  daInitialAssessment: {
    bypassAssessmentSummary: false,
    bypassReview: true,
    linkedAssignmentTitle: 'Care Onboarding',
    hideInClientActivities: true,
    hideInstructionsIfHasPrereq: true,
    initialPageIfHasPrereq: 1,
    linkedStatusAssignments: [
      'daCareConsent',
      DA_CARE_ONBOARDING_HPI_PAYMENT_CONTENT_NAME,
      [
        'daAdolescentClientIntake',
        'daAdolescentGuardianClientIntake',
        'daChildClientIntake',
        'daCouplesClientIntake',
        'daFamilyClientIntake',
        'daIndividualClientIntake',
      ],
      'daInitialAssessment',
    ],
    shownParentActivity: [
      'daAdolescentClientIntake',
      'daAdolescentGuardianClientIntake',
      'daChildClientIntake',
      'daCouplesClientIntake',
      'daFamilyClientIntake',
      'daIndividualClientIntake',
    ],
  },
  lcttCareConsent: {
    bypassAssessmentSummary: true,
    bypassReview: true,
    customActivityIntroduction: 'CareOnboardingHeader',
    customErrorMessage: careConsentFormError,
    hideInClientActivities: true,
    linkedAssignmentTitle: 'Care Onboarding',
    hideProgressBar: true,
    linkedStatusAssignments: ['lcttCareConsent', 'lcttClientIntake', 'lcttInitialAssessment'],
    singlePageContent: true,
    shownParentActivity: 'lcttClientIntake',
  },
  lcttClientIntake: {
    bypassAssessmentSummary: true,
    bypassReview: true,
    linkedAssignmentTitle: 'Care Onboarding',
    hideInstructionsIfHasPrereq: true,
    initialPageIfHasPrereq: 1,
    linkedStatusAssignments: ['lcttCareConsent', 'lcttClientIntake', 'lcttInitialAssessment'],
  },
  lcttInitialAssessment: {
    bypassAssessmentSummary: false,
    bypassReview: true,
    linkedAssignmentTitle: 'Care Onboarding',
    hideInClientActivities: true,
    hideInstructionsIfHasPrereq: true,
    initialPageIfHasPrereq: 1,
    linkedStatusAssignments: ['lcttCareConsent', 'lcttClientIntake', 'lcttInitialAssessment'],
    linkedVideo: 'baseYourTherapyWithLyra',
    shownParentActivity: 'lcttClientIntake',
  },
  sscCareConsent: {
    bypassAssessmentSummary: true,
    bypassReview: true,
    customActivityIntroduction: 'CareOnboardingHeader',
    customErrorMessage: careConsentFormError,
    hideInClientActivities: true,
    linkedAssignmentTitle: 'Care Onboarding',
    hideProgressBar: true,
    linkedStatusAssignments: ['sscCareConsent', 'sscClientIntake', 'sscBaselineMetadata'],
    singlePageContent: true,
    shownParentActivity: 'sscClientIntake',
  },
  sscClientIntake: {
    bypassAssessmentSummary: true,
    bypassReview: true,
    linkedAssignmentTitle: 'Care Onboarding',
    hideInstructionsIfHasPrereq: true,
    initialPageIfHasPrereq: 1,
    linkedStatusAssignments: ['sscCareConsent', 'sscClientIntake', 'sscBaselineMetadata'],
  },
  sscBaselineMetadata: {
    bypassAssessmentSummary: false,
    bypassReview: true,
    linkedAssignmentTitle: 'Care Onboarding',
    hideInClientActivities: true,
    hideInstructionsIfHasPrereq: true,
    initialPageIfHasPrereq: 1,
    linkedStatusAssignments: ['sscCareConsent', 'sscClientIntake', 'sscBaselineMetadata'],
    linkedVideo: 'baseYourTherapyWithLyra',
    shownParentActivity: 'sscClientIntake',
  },
  dbtCareConsent: {
    bypassAssessmentSummary: true,
    bypassReview: true,
    customActivityIntroduction: 'CareOnboardingHeader',
    customErrorMessage: careConsentFormError,
    hideInClientActivities: true,
    linkedAssignmentTitle: 'Care Onboarding',
    hideProgressBar: true,
    linkedStatusAssignments: ['dbtCareConsent', 'dbtClientIntake', 'dbtInitialAssessment'],
    singlePageContent: true,
    shownParentActivity: 'dbtClientIntake',
  },
  dbtClientIntake: {
    bypassAssessmentSummary: true,
    bypassReview: true,
    linkedAssignmentTitle: 'Care Onboarding',
    hideInstructionsIfHasPrereq: true,
    initialPageIfHasPrereq: 1,
    linkedStatusAssignments: ['dbtCareConsent', 'dbtClientIntake', 'dbtInitialAssessment'],
  },
  dbtInitialAssessment: {
    bypassAssessmentSummary: false,
    bypassReview: true,
    linkedAssignmentTitle: 'Care Onboarding',
    hideInClientActivities: true,
    hideInstructionsIfHasPrereq: true,
    initialPageIfHasPrereq: 1,
    linkedStatusAssignments: ['dbtCareConsent', 'dbtClientIntake', 'dbtInitialAssessment'],
    shownParentActivity: 'dbtClientIntake',
  },
  dbttCareConsent: {
    bypassAssessmentSummary: true,
    bypassReview: true,
    customActivityIntroduction: 'CareOnboardingHeader',
    customErrorMessage: careConsentFormError,
    hideInClientActivities: true,
    linkedAssignmentTitle: 'Care Onboarding',
    hideProgressBar: true,
    linkedStatusAssignments: [
      'dbttCareConsent',
      ['dbttParentIntake', 'dbttTeenIntake'],
      ['dbttParentInitialAssessment', 'dbttInitialAssessment'],
    ],
    shownParentActivity: ['dbttParentIntake', 'dbttTeenIntake'],
    singlePageContent: true,
  },
  dbttParentIntake: {
    bypassAssessmentSummary: true,
    bypassReview: true,
    linkedAssignmentTitle: 'Care Onboarding',
    hideInstructionsIfHasPrereq: true,
    initialPageIfHasPrereq: 1,
    linkedStatusAssignments: ['dbttCareConsent', 'dbttParentIntake', 'dbttParentInitialAssessment'],
  },
  dbttParentInitialAssessment: {
    bypassAssessmentSummary: false,
    bypassReview: true,
    linkedAssignmentTitle: 'Care Onboarding',
    hideInClientActivities: true,
    hideInstructionsIfHasPrereq: true,
    initialPageIfHasPrereq: 1,
    linkedStatusAssignments: ['dbttCareConsent', 'dbttParentIntake', 'dbttParentInitialAssessment'],
    shownParentActivity: 'dbttParentIntake',
  },
  dbttTeenIntake: {
    bypassAssessmentSummary: true,
    bypassReview: true,
    linkedAssignmentTitle: 'Care Onboarding',
    hideInstructionsIfHasPrereq: true,
    initialPageIfHasPrereq: 1,
    linkedStatusAssignments: ['dbttCareConsent', 'dbttTeenIntake', 'dbttInitialAssessment'],
  },
  dbttInitialAssessment: {
    bypassAssessmentSummary: false,
    bypassReview: true,
    linkedAssignmentTitle: 'Care Onboarding',
    hideInClientActivities: true,
    hideInstructionsIfHasPrereq: true,
    initialPageIfHasPrereq: 1,
    linkedStatusAssignments: ['dbttCareConsent', 'dbttTeenIntake', 'dbttInitialAssessment'],
    shownParentActivity: 'dbttTeenIntake',
  },
  nscCareConsent: {
    bypassAssessmentSummary: true,
    bypassReview: true,
    customActivityIntroduction: 'CareOnboardingHeader',
    customErrorMessage: careConsentFormError,
    hideInClientActivities: true,
    linkedAssignmentTitle: 'Care Onboarding',
    hideProgressBar: true,
    linkedStatusAssignments: ['nscCareConsent', 'nscClientIntake', 'nscBaselineMetadata'],
    singlePageContent: true,
    shownParentActivity: 'nscClientIntake',
  },
  nscClientIntake: {
    bypassAssessmentSummary: true,
    bypassReview: true,
    linkedAssignmentTitle: 'Care Onboarding',
    hideInstructionsIfHasPrereq: true,
    initialPageIfHasPrereq: 1,
    linkedStatusAssignments: ['nscCareConsent', 'nscClientIntake', 'nscBaselineMetadata'],
  },
  nscBaselineMetadata: {
    bypassAssessmentSummary: false,
    bypassReview: true,
    linkedAssignmentTitle: 'Care Onboarding',
    hideInClientActivities: true,
    hideInstructionsIfHasPrereq: true,
    initialPageIfHasPrereq: 1,
    linkedStatusAssignments: ['nscCareConsent', 'nscClientIntake', 'nscBaselineMetadata'],
    shownParentActivity: 'nscClientIntake',
  },
  cgtCareConsent: {
    bypassAssessmentSummary: true,
    bypassReview: true,
    customActivityIntroduction: 'CareOnboardingHeader',
    customErrorMessage: careConsentFormError,
    hideInClientActivities: true,
    linkedAssignmentTitle: 'Care Onboarding',
    hideProgressBar: true,
    linkedStatusAssignments: ['cgtCareConsent', 'cgtClientIntake', 'cgtBaselineMetadata'],
    singlePageContent: true,
    shownParentActivity: 'cgtClientIntake',
  },
  cgtClientIntake: {
    bypassAssessmentSummary: true,
    bypassReview: true,
    linkedAssignmentTitle: 'Care Onboarding',
    hideInstructionsIfHasPrereq: true,
    initialPageIfHasPrereq: 1,
    linkedStatusAssignments: ['cgtCareConsent', 'cgtClientIntake', 'cgtBaselineMetadata'],
  },
  cgtBaselineMetadata: {
    bypassAssessmentSummary: false,
    bypassReview: true,
    linkedAssignmentTitle: 'Care Onboarding',
    hideInClientActivities: true,
    hideInstructionsIfHasPrereq: true,
    initialPageIfHasPrereq: 1,
    linkedStatusAssignments: ['cgtCareConsent', 'cgtClientIntake', 'cgtBaselineMetadata'],
    shownParentActivity: 'cgtClientIntake',
  },
  mcCareConsent: {
    bypassAssessmentSummary: true,
    bypassReview: true,
    customActivityIntroduction: 'CareOnboardingHeader',
    customErrorMessage: careConsentFormError,
    hideInClientActivities: true,
    linkedAssignmentTitle: 'Care Onboarding',
    hideProgressBar: true,
    linkedStatusAssignments: ['mcCareConsent', 'mcClientIntake'],
    singlePageContent: true,
    shownParentActivity: 'mcClientIntake',
  },
  mcClientIntake: {
    bypassAssessmentSummary: true,
    bypassReview: true,
    linkedAssignmentTitle: 'Care Onboarding',
    hideInstructionsIfHasPrereq: true,
    initialPageIfHasPrereq: 1,
    linkedStatusAssignments: ['mcCareConsent', 'mcClientIntake'],
  },
}
